import type { Tramo, TramoSemanal } from '../api/ops/disponibilidad';

/**
 * Lógica sin estilos del horario (3.1 y 3.2). La API guarda cada tramo en hora local con su duración (`start_local`,
 * `starts_next_day`, `duration_minutes`, G247); la pantalla lo enseña como «Desde» y «Hasta» sobre un deslizador que va
 * de las 05:00 a las 07:00 del día siguiente. Aquí se pasa de una forma a otra, se juntan los días con los mismos tramos
 * en bloques y se buscan los tramos que se pisan (también con la madrugada siguiente).
 *
 * **Nunca** se calcula UTC ni el cambio de hora: todo es hora local del calendario y lo demás lo hace el servidor. Las
 * fechas son `AAAA-MM-DD` locales (`LocalDate`) y se suman como fechas de calendario.
 */

/** Un tramo en minutos desde las 00:00 del día del tramo: `desde` ≥ 1440 = empieza en la madrugada siguiente («+1»). */
export interface Rango {
    desde: number;
    hasta: number;
}

/** Días con los mismos tramos (0 = lunes … 6 = domingo). Solo es la forma de editar: la API guarda la semana día a día. */
export interface Bloque {
    clave: string;
    dias: number[];
    tramos: Rango[];
}

/** Siete días (0 = lunes) con sus tramos ordenados; un día sin tramos está cerrado. */
export type Semana = Rango[][];

export const DIA = 1440;
/** Límites del deslizador: de 05:00 a 07:00 del día siguiente, de 15 en 15 minutos. */
export const DESLIZADOR = { min: 300, max: 1860, paso: 15 } as const;
/** Duración de un tramo: de 1 minuto a 24 horas (`DurationMinutes`). */
export const DURACION_MAXIMA = DIA;
/** Como mucho tres tramos por día (comida, tarde y noche). */
export const TRAMOS_POR_BLOQUE = 3;

export const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'] as const;
export const DIAS_CORTOS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'] as const;
const DIAS_ABREV = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;

const dos = (n: number) => String(n).padStart(2, '0');

/** «21:30» → 1290. */
export function aMinutos(hora: string): number {
    const [h, m] = hora.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}

/** 1290 → «21:30» (sin el día: 1470 → «00:30»). */
export function aHora(minutos: number): string {
    const m = ((minutos % DIA) + DIA) % DIA;
    return `${dos(Math.floor(m / 60))}:${dos(m % 60)}`;
}

export function rangoDeTramo(t: Tramo): Rango {
    const desde = aMinutos(t.start_local) + (t.starts_next_day ? DIA : 0);
    return { desde, hasta: desde + t.duration_minutes };
}

export function tramoDeRango(r: Rango): Tramo {
    return { start_local: aHora(r.desde), starts_next_day: r.desde >= DIA, duration_minutes: r.hasta - r.desde };
}

const ordenar = (tramos: readonly Rango[]) => [...tramos].sort((a, b) => a.desde - b.desde || a.hasta - b.hasta);

/** La semana de los tramos de la API (`weekday` 1 = lunes … 7 = domingo). */
export function semanaDeTramos(tramos: readonly TramoSemanal[]): Semana {
    const semana: Semana = [[], [], [], [], [], [], []];
    for (const t of tramos) {
        semana[t.weekday - 1]?.push(rangoDeTramo(t));
    }
    return semana.map(ordenar);
}

/** Los tramos que se mandan a la API, por día y hora. */
export function tramosDeSemana(semana: Semana): TramoSemanal[] {
    return semana.flatMap((dia, i) => ordenar(dia).map((r) => ({ ...tramoDeRango(r), weekday: i + 1 })));
}

const firma = (tramos: readonly Rango[]) => ordenar(tramos).map((r) => `${String(r.desde)}-${String(r.hasta)}`).join(',');

/** Junta en un bloque los días con los mismos tramos, en el orden de la semana. Los días cerrados no salen. */
export function bloquesDeSemana(semana: Semana): Bloque[] {
    const bloques: (Bloque & { firma: string })[] = [];
    semana.forEach((dia, i) => {
        if (dia.length === 0) {
            return;
        }
        const f = firma(dia);
        const igual = bloques.find((b) => b.firma === f);
        if (igual !== undefined) {
            igual.dias.push(i);
        } else {
            bloques.push({ clave: `b${String(i)}`, dias: [i], tramos: ordenar(dia), firma: f });
        }
    });
    return bloques.map(({ clave, dias, tramos }) => ({ clave, dias, tramos }));
}

/** La semana de unos bloques: cada día con los tramos de su bloque; los que no están en ninguno, cerrados. */
export function semanaDeBloques(bloques: readonly Bloque[]): Semana {
    const semana: Semana = [[], [], [], [], [], [], []];
    for (const b of bloques) {
        for (const d of b.dias) {
            semana[d] = ordenar(b.tramos);
        }
    }
    return semana;
}

/** Los días (0 = lunes) que no están en ningún bloque. */
export function diasCerrados(bloques: readonly Bloque[]): number[] {
    const usados = new Set(bloques.flatMap((b) => b.dias));
    return DIAS.map((_, i) => i).filter((i) => !usados.has(i));
}

/** Los días que cambian de una semana a otra. */
export function diasCambiados(antes: Semana, despues: Semana): number[] {
    return DIAS.map((_, i) => i).filter((i) => firma(antes[i] ?? []) !== firma(despues[i] ?? []));
}

export function mismaSemana(a: Semana, b: Semana): boolean {
    return diasCambiados(a, b).length === 0;
}

const unir = (partes: readonly string[]) => (partes.length < 2 ? partes.join('') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1] ?? ''}`);

/** «lunes a viernes», «viernes y sábado», «lunes, miércoles y viernes». */
export function textoDias(dias: readonly number[]): string {
    const d = [...dias].sort((a, b) => a - b);
    const partes: string[] = [];
    let i = 0;
    while (i < d.length) {
        let j = i;
        while (j + 1 < d.length && (d[j + 1] ?? -1) === (d[j] ?? -2) + 1) {
            j += 1;
        }
        const primero = d[i] ?? 0;
        const ultimo = d[j] ?? 0;
        if (j - i >= 2) {
            partes.push(`${DIAS[primero] ?? ''} a ${DIAS[ultimo] ?? ''}`);
        } else {
            partes.push(...d.slice(i, j + 1).map((x) => DIAS[x] ?? ''));
        }
        i = j + 1;
    }
    return unir(partes);
}

/** «de 20:00 a 02:00 (+1)»; un tramo que empieza de madrugada, «de 00:30 (+1) a 06:00 (+1)». Acabar a las 00:00 no es «+1». */
export function textoTramo(r: Rango): string {
    return `de ${aHora(r.desde)}${r.desde >= DIA ? ' (+1)' : ''} a ${aHora(r.hasta)}${r.hasta > DIA ? ' (+1)' : ''}`;
}

/** «4 horas», «1 hora y 30 minutos», «45 minutos». */
export function textoDuracion(minutos: number): string {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    const horas = h === 0 ? '' : `${String(h)} ${h === 1 ? 'hora' : 'horas'}`;
    const mins = m === 0 ? '' : `${String(m)} minutos`;
    return [horas, mins].filter((x) => x !== '').join(' y ');
}

/**
 * Errores de los tramos de una semana, por día y tramo ('' si está bien): duración nula, tramos del mismo día que se
 * pisan y tramos que pasan de medianoche y se juntan con el día siguiente (de domingo a lunes también).
 */
export function erroresDeSemana(semana: Semana): string[][] {
    const todos = semana.flatMap((dia, d) => dia.map((r, t) => ({ d, t, r, inicio: d * DIA + r.desde, fin: d * DIA + r.hasta })));
    const SEMANA = 7 * DIA;
    return semana.map((dia, d) =>
        dia.map((r, t) => {
            if (r.hasta <= r.desde) {
                return 'Elige una hora distinta de la de abrir.';
            }
            if (r.hasta - r.desde > DURACION_MAXIMA) {
                return 'Un tramo dura 24 horas como mucho.';
            }
            const inicio = d * DIA + r.desde;
            const fin = d * DIA + r.hasta;
            for (const o of todos) {
                if (o.d === d && o.t === t) {
                    continue;
                }
                for (const salto of [0, SEMANA, -SEMANA]) {
                    const oi = o.inicio + salto;
                    const of = o.fin + salto;
                    if (!(inicio < of && oi < fin)) {
                        continue;
                    }
                    if (o.d === d && salto === 0) {
                        return `Se junta con el tramo ${textoTramo(o.r)}.`;
                    }
                    // Lo pinta el tramo que empieza antes (el que invade el día siguiente).
                    if (oi > inicio) {
                        return `Se junta con el ${DIAS[o.d] ?? ''}, que abre a las ${aHora(o.r.desde)}.`;
                    }
                }
            }
            return '';
        }),
    );
}

/** Los errores de cada tramo de cada bloque: el primero que salga en alguno de sus días. */
export function erroresDeBloques(bloques: readonly Bloque[]): string[][] {
    const semana = semanaDeBloques(bloques);
    const errores = erroresDeSemana(semana);
    return bloques.map((b) =>
        b.tramos.map((r) => {
            for (const d of b.dias) {
                const i = (semana[d] ?? []).findIndex((x) => x.desde === r.desde && x.hasta === r.hasta);
                const e = errores[d]?.[i] ?? '';
                if (e !== '') {
                    return e;
                }
            }
            return '';
        }),
    );
}

export function hayErrores(errores: readonly (readonly string[])[]): boolean {
    return errores.some((x) => x.some((e) => e !== ''));
}

// ---- Escribir la hora (G247) ----

/** «2130», «21:30», «21.30», «21h», «930», «9» → minutos (0 a 1440); null si no es una hora. */
export function leerHora(texto: string): number | null {
    const t = texto.trim().replace(/[.,hH ]+/g, ':').replace(/:$/, '');
    let h: number;
    let m: number;
    const conPuntos = /^(\d{1,2}):(\d{0,2})$/.exec(t);
    if (conPuntos !== null) {
        h = Number(conPuntos[1]);
        m = conPuntos[2] === '' || conPuntos[2] === undefined ? 0 : Number(conPuntos[2]);
    } else if (/^\d{1,4}$/.test(t)) {
        if (t.length <= 2) {
            h = Number(t);
            m = 0;
        } else {
            const q = t.padStart(4, '0');
            h = Number(q.slice(0, 2));
            m = Number(q.slice(2));
        }
    } else {
        return null;
    }
    if (m >= 60 || h * 60 + m > DIA) {
        return null;
    }
    return h * 60 + m;
}

/** ¿Está escrita la hora entera (4 cifras o «21:30»)? Entonces se aplica sin esperar a salir del campo. */
export function horaCompleta(texto: string): boolean {
    const t = texto.trim();
    return /^\d{4}$/.test(t) || /^\d{1,2}[:.h]\d{2}$/.test(t);
}

export const ERROR_HORA = 'Escribe la hora así: 21:30 o 2130.';

/**
 * «Desde» escrito: de 05:00 a 23:59 es ese día; antes de las 05:00, la madrugada siguiente («+1», `starts_next_day`,
 * G247). Mantiene la duración del tramo.
 */
export function desdeEscrito(texto: string, rango: Rango): Rango | string {
    const m = leerHora(texto);
    if (m === null) {
        return ERROR_HORA;
    }
    const desde = m % DIA < DESLIZADOR.min ? (m % DIA) + DIA : m % DIA;
    return { desde, hasta: desde + Math.min(DURACION_MAXIMA, Math.max(DESLIZADOR.paso, rango.hasta - rango.desde)) };
}

/** «Hasta» escrito: la primera vez que llega esa hora después de «Desde»; cualquier duración hasta 24 horas (G247). */
export function hastaEscrito(texto: string, rango: Rango): Rango | string {
    const m = leerHora(texto);
    if (m === null) {
        return ERROR_HORA;
    }
    let hasta = m % DIA;
    while (hasta <= rango.desde) {
        hasta += DIA;
    }
    return { desde: rango.desde, hasta };
}

/** Lo que deja el deslizador: el inicio entre 05:00 y 06:45 del día siguiente y como mínimo un paso de duración. */
export function ajustarRango(r: Rango): Rango {
    const desde = Math.min(Math.max(r.desde, DESLIZADOR.min), DESLIZADOR.max - DESLIZADOR.paso);
    const hasta = Math.min(Math.max(r.hasta, desde + DESLIZADOR.paso), desde + DURACION_MAXIMA);
    return { desde, hasta };
}

/** Un tramo nuevo detrás del último (una hora después, de 4 horas), o el de por defecto. */
export function tramoNuevo(tramos: readonly Rango[], porDefecto: Rango): Rango {
    const ultimo = tramos[tramos.length - 1];
    if (ultimo === undefined) {
        return porDefecto;
    }
    const desde = Math.min(ultimo.hasta + 60, DESLIZADOR.max - 4 * 60);
    return { desde, hasta: desde + 4 * 60 };
}

// ---- Fechas locales (AAAA-MM-DD) ----

const partes = (iso: string) => iso.split('-').map(Number) as [number, number, number];
const comoFecha = (iso: string) => {
    const [y, m, d] = partes(iso);
    return new Date(Date.UTC(y, m - 1, d));
};
const deFecha = (f: Date) => `${String(f.getUTCFullYear())}-${dos(f.getUTCMonth() + 1)}-${dos(f.getUTCDate())}`;

export function sumarDias(iso: string, dias: number): string {
    const f = comoFecha(iso);
    f.setUTCDate(f.getUTCDate() + dias);
    return deFecha(f);
}

/** 0 = lunes … 6 = domingo. */
export function diaDeLaSemana(iso: string): number {
    return (comoFecha(iso).getUTCDay() + 6) % 7;
}

/** «miércoles 14 de octubre» (con el año si no es el de `anio`). */
export function fechaLarga(iso: string, anio?: number): string {
    const [y, m, d] = partes(iso);
    return `${DIAS[diaDeLaSemana(iso)] ?? ''} ${String(d)} de ${MESES[m - 1] ?? ''}${anio !== undefined && y !== anio ? ` de ${String(y)}` : ''}`;
}

/** «Mié 14 oct» (con el año si no es el de `anio`). */
export function fechaCorta(iso: string, anio?: number): string {
    const [y, m, d] = partes(iso);
    return `${DIAS_ABREV[diaDeLaSemana(iso)] ?? ''} ${String(d)} ${(MESES[m - 1] ?? '').slice(0, 3)}${anio !== undefined && y !== anio ? ` ${String(y)}` : ''}`;
}

/** Día y mes de la baldosa de una fecha: «31» y «oct». */
export function baldosa(iso: string): { dia: string; mes: string } {
    const [, m, d] = partes(iso);
    return { dia: String(d), mes: (MESES[m - 1] ?? '').slice(0, 3) };
}

export function anioDe(iso: string): number {
    return partes(iso)[0];
}

/**
 * Hoy en la zona del calendario (`effective_timezone`) como fecha local, para el «Se aplica desde» y el rango de días
 * especiales. Solo lee la fecha que da el navegador para esa zona; sin zona válida, la del navegador.
 */
export function hoyEn(zona: string | null | undefined, ahora: Date = new Date()): string {
    try {
        const p = new Intl.DateTimeFormat('en-CA', { timeZone: zona ?? undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(ahora);
        const v = (t: string) => p.find((x) => x.type === t)?.value ?? '';
        return `${v('year')}-${v('month')}-${v('day')}`;
    } catch {
        return `${String(ahora.getFullYear())}-${dos(ahora.getMonth() + 1)}-${dos(ahora.getDate())}`;
    }
}

/** Fecha y hora locales de un instante en la zona del calendario (solo para enseñarlo, p. ej. un cierre). */
export function enZona(instante: string, zona: string | null | undefined): { fecha: string; hora: string } {
    const f = new Date(instante);
    try {
        const p = new Intl.DateTimeFormat('en-CA', { timeZone: zona ?? undefined, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(f);
        const v = (t: string) => p.find((x) => x.type === t)?.value ?? '';
        return { fecha: `${v('year')}-${v('month')}-${v('day')}`, hora: `${v('hour')}:${v('minute')}` };
    } catch {
        return { fecha: deFecha(f), hora: `${dos(f.getUTCHours())}:${dos(f.getUTCMinutes())}` };
    }
}
